import { matchesCategory, reportingTransactions, transactionAllocations } from "../lib/allocations";
import { matchesReportingScope } from "../lib/reporting";
import type { ReportingScope } from "../types/reporting";
import { useMemo } from "react";
import type { BudgetOwner, DashboardData, OwnerFilter } from "../types";
import { resolveBudget, budgetedPairs, OVERALL_CATEGORY } from "../lib/budgets";
import { isExpense, isIncome, isSpendable, isEarnable, reportableBase, total } from "../lib/finance";
import { ledgerMonths, uncategorizedCount as countUncategorized } from "../lib/coverage";
import { reconciliationMonthLabel } from "../lib/reconciliation";

// Everything the screens show that is worked out from the ledger and the current filters: the
// month's entries and totals, budgets, the category chart, the filtered history and the months to
// pick from. Pure calculation, memoised on what it reads; the state it reads stays in App.

export interface LedgerFilters {
  reporting?: ReportingScope | null;
  query: string;
  dateFilter: string;
  accountFilter: string;
  category: string;
  subcategory: string | null;
  type: string;
  tripFilter?: string;
  reviewUncategorized: boolean;
}

export function useLedgerView(
  data: DashboardData | null,
  { owner, month, today, chartCategory }: { owner: OwnerFilter; month: string; today: string; chartCategory: string | null },
  { query, dateFilter, accountFilter, category, subcategory, type, tripFilter, reviewUncategorized, reporting }: LedgerFilters,
  historyData: DashboardData | null = data,
) {
  const selectedAccounts =
    data?.accounts.filter((a) => owner === "All" || a.owner === owner) || [];
  const monthTransactions = useMemo(
    () =>
      data?.transactions.filter(
        (t) =>
          t.date.startsWith(month) &&
          (owner === "All" || t.owner === owner || t.toOwner === owner),
      ) || [],
    [data, month, owner],
  );
  // For the overview's "needs categorizing" prompt: counted once per load, not twice per render.
  const uncategorizedCount = useMemo(() => (data ? countUncategorized(data, owner) : 0), [data, owner]);
  const expenses = useMemo(() => monthTransactions.filter(isSpendable), [monthTransactions]),
    spending = total(expenses),
    income = total(monthTransactions.filter(isEarnable));
  const budgetOwner: BudgetOwner = owner === "All" ? "Household" : owner;
  const budgets = data?.budgets || [];
  const overallBudget = data
    ? resolveBudget(budgets, budgetOwner, OVERALL_CATEGORY, month)
    : undefined;
  const categoryBudgetRows = data
    ? [
        ...new Set(
          budgetedPairs(budgets)
            .filter((p) => p.owner === budgetOwner && p.category !== OVERALL_CATEGORY)
            .map((p) => p.category),
        ),
      ]
        .map((category) => ({
          category,
          budget: resolveBudget(budgets, budgetOwner, category, month),
        }))
        .filter(
          (r): r is { category: string; budget: NonNullable<typeof r.budget> } =>
            !!r.budget && r.budget.amount !== null,
        )
    : [];
  const chartExpenses = useMemo(
    () =>
      chartCategory === null
        ? reportingTransactions(expenses)
        : reportingTransactions(expenses).filter((t) => t.category === chartCategory),
    [expenses, chartCategory],
  );
  const chartTotal = total(chartExpenses);
  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of chartExpenses) {
      const label =
        chartCategory === null ? t.category : t.subcategory || "Unspecified";
      const value = reportableBase(t);
      if (value !== null) map.set(label, (map.get(label) || 0) + value);
    }
    return [...map].sort((a, b) => b[1] - a[1]);
  }, [chartExpenses, chartCategory]);
  const filtered = useMemo(() => {
    const needle = query.toLowerCase();
    const hasTrip = tripFilter && tripFilter !== "All";
    const payees = historyData?.payees;
    return historyData?.transactions.filter(
      (t) =>
        (reviewUncategorized || type === "Pending" || hasTrip || t.date.startsWith(month)) &&
        (!reporting || matchesReportingScope(t, today, reporting, payees)) &&
        (!dateFilter || t.date.startsWith(dateFilter)) &&
        (!hasTrip || t.tripId === tripFilter) &&
        (owner === "All" || t.owner === owner || t.toOwner === owner) &&
        (accountFilter === "All" ||
          t.accountId === accountFilter ||
          t.toAccountId === accountFilter) &&
        matchesCategory(t, category, subcategory) &&
        (!reviewUncategorized || (!t.pendingKey && isSpendable(t) && matchesCategory(t, "Uncategorized"))) &&
        (type === "All" ||
          (type === "Pending"
            ? !!t.pending
            : type === "Expense"
            ? isExpense(t)
            : type === "Income"
              ? isIncome(t)
              : !isExpense(t) && !isIncome(t))) &&
        `${t.title} ${t.description} ${t.payeeId ? payees?.find((p) => p.id === t.payeeId)?.name || "" : ""} ${transactionAllocations(t).map(p => `${p.category} ${p.subcategory}`).join(" ")} ${t.notes} ${t.tripDestination || ""} ${t.id}`
          .toLowerCase()
          .includes(needle),
    ) || [];
  }, [historyData, reviewUncategorized, month, dateFilter, owner, accountFilter, category, subcategory, type, tripFilter, query, reporting, today]);
  const months = useMemo(
    () =>
      Array.from(
        new Set([
          today.slice(0, 7),
          month,
          ...(historyData ? ledgerMonths(historyData) : []),
          // Entries waiting to sync are in the list but not yet in the ledger's own months.
          ...(historyData?.transactions.map((t) => t.date.slice(0, 7)) || []),
        ]),
      )
        .sort()
        .reverse(),
    [historyData, month, today],
  );
  const monthName = reconciliationMonthLabel(month);
  return {
    selectedAccounts, monthTransactions, uncategorizedCount, expenses, spending, income,
    budgetOwner, overallBudget, categoryBudgetRows, chartExpenses, chartTotal, categories,
    filtered, months, monthName,
  };
}
